import * as React from 'react';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import type { Bounds } from '../../Reducers/Configurator/types';
import { worldToScreen } from '../../utils/textMetrics';
import type { VisibleRect } from './useEditorViewport';
import { XXL_COLORS } from '../../theme/colors';

const STROKE_PX = 2;
const COLOR = XXL_COLORS.accent;
const DELETE_ICON_SIZE = 22;
const DELETE_ICON_RADIUS = DELETE_ICON_SIZE / 2;

type SelectionOverlayProps = {
  bounds: Bounds | null;
  selectedId: string | null;
  viewRect: VisibleRect;
  pxPerMm: number;
  onDelete?: (id: string) => void;
};

export default function SelectionOverlay({
  bounds,
  selectedId,
  viewRect,
  pxPerMm,
  onDelete,
}: SelectionOverlayProps) {
  if (!bounds || bounds.width <= 0 || bounds.height <= 0 || pxPerMm <= 0) {
    return null;
  }

  const topLeft = worldToScreen(bounds.x, bounds.y, viewRect, pxPerMm);
  const bottomRight = worldToScreen(bounds.x + bounds.width, bounds.y + bounds.height, viewRect, pxPerMm);

  const x = topLeft.x;
  const y = topLeft.y;
  const width = bottomRight.x - topLeft.x;
  const height = bottomRight.y - topLeft.y;
  const showDelete = Boolean(selectedId && onDelete);
  const deleteCenterX = bottomRight.x;
  const deleteCenterY = topLeft.y;

  const handleDeletePointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedId && onDelete) {
      onDelete(selectedId);
    }
  };

  if (!selectedId) {
    return null;
  }

  return (
    <g className="no-export">
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="none"
        stroke={COLOR}
        strokeWidth={STROKE_PX}
        strokeDasharray="6 3"
        pointerEvents="none"
      />

      {showDelete && (
        <g
          data-editor-delete
          transform={`translate(${deleteCenterX - DELETE_ICON_RADIUS}, ${deleteCenterY - DELETE_ICON_RADIUS})`}
          onPointerDown={handleDeletePointerDown}
          onPointerDownCapture={handleDeletePointerDown}
          onClick={handleDeleteClick}
          style={{ cursor: 'pointer' }}
          pointerEvents="all"
          role="button"
          aria-label={t('common.deleteSelectedItem')}
          tabIndex={0}
        >
          <circle cx={8} cy={9} r={DELETE_ICON_RADIUS} fill="#ffffff" stroke={COLOR} strokeWidth={1.5} />
          <path
            d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
            fill={COLOR}
            transform={`translate(${DELETE_ICON_RADIUS - 12}, ${DELETE_ICON_RADIUS - 12}) scale(0.75)`}
          />
        </g>
      )}
    </g>
  );
}
